import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import Dialog, { DialogError } from '../../components/Dialog.jsx';
import Notice from '../../components/Notice.jsx';
import PageState from '../../components/PageState.jsx';
import RoleChips from '../../components/RoleChips.jsx';
import Toggle from '../../components/Toggle.jsx';
import { api } from '../../lib/api.js';
import { LIVE_REASON_TEXT } from '../../lib/live-status.js';
import { SOURCE, buildRuleRows, liveTogglePatch, ruleSearchText } from '../../lib/rule-rows.js';
import { CATEGORY_LABELS } from '../../lib/watch-format.js';
import { customerSectionPath } from '../../lib/navigation.js';
import { Dot } from '../../components/StatusWord.jsx';
import LiveWatchSettingsDialog from './LiveWatchSettingsDialog.jsx';
import RuleEditor from './RuleEditor.jsx';

function Detail({ row, classicCatalog }) {
  if (row.source === 'daily') {
    const ev = classicCatalog?.find((e) => e.id === row.rule.eventId);
    return (
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
        {ev?.description ? <><dt className="text-slate-600">What it detects</dt><dd>{ev.description}</dd></> : null}
        <dt className="text-slate-600">Recipients</dt>
        <dd>{row.audience.map((a) => a.label).join(', ') || 'None'}</dd>
        <dt className="text-slate-600">When</dt>
        <dd>{row.cadence.hint}</dd>
      </dl>
    );
  }
  return (
    <p className="text-sm text-slate-800">
      {row.cadence.hint} Evaluated every 15 minutes against the Microsoft Entra audit log.
    </p>
  );
}

export default function NotificationRules({ customerId, data, config, operatorAccount, onChanged }) {
  const classic = data.classic;
  const masterOn = classic?.notificationsEnabled === true;
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [testEmail, setTestEmail] = useState(null); // null closed
  const [busyTest, setBusyTest] = useState(false);
  // Optimistic switches: { rowId: next } shown at once, dropped when the reload lands or the save fails (rollback).
  const [pending, setPending] = useState({});
  const inFlightRef = useRef(new Set());
  const [busyRule, setBusyRule] = useState(false);
  const openerRef = useRef(null);
  const newRuleRef = useRef(null);
  const testRef = useRef(null);
  const tableWrapRef = useRef(null);
  const rowFocusRef = useRef(null);
  const savingRef = useRef(false);
  const [editError, setEditError] = useState(null);

  const rows = useMemo(
    () => buildRuleRows({ rules: classic?.rules, classicCatalog: classic?.catalog, watch: data.watch, alertEmail: config?.alertEmail ?? '', sources: classic?.sources ?? null }),
    [classic, data.watch, config?.alertEmail],
  );

  const clearPending = (id) => setPending((p) => { const { [id]: _drop, ...rest } = p; return rest; });

  async function toggle(row, next) {
    // One save per row at a time; the switch already shows the value being saved.
    if (inFlightRef.current.has(row.id)) return;
    inFlightRef.current.add(row.id);
    setNotice(null);
    setPending((p) => ({ ...p, [row.id]: next }));
    try {
      if (row.source === 'daily') {
        await api.updateNotification(customerId, row.rule.ruleId, { enabled: next });
      } else {
        const r = await api.patchWatchSettings(customerId, liveTogglePatch(row, next));
        if (r?.live?.reason) setNotice({ tone: 'warn', text: LIVE_REASON_TEXT[r.live.reason] ?? 'Saved. Live watch did not change.' });
      }
    } catch (e) {
      // The save failed: roll the switch back and say why.
      setNotice({ tone: 'error', text: e.message });
      clearPending(row.id);
      inFlightRef.current.delete(row.id);
      return;
    }
    try {
      await onChanged();
      clearPending(row.id);
    } catch {
      // Saved, but the reload failed: keep showing the saved value (it stays until fresh data arrives).
      setNotice({ tone: 'warn', text: 'Saved. The list could not refresh; reload to see the latest.' });
    } finally {
      inFlightRef.current.delete(row.id);
    }
  }

  // Fresh data replaces any saved value still shown after a failed reload (a save in flight keeps its value).
  useEffect(() => {
    setPending((p) => Object.fromEntries(Object.entries(p).filter(([id]) => inFlightRef.current.has(id))));
  }, [classic, data.watch]);

  // Focus returns to the row's "Actions for <row>" button, else the table region (never an unrelated toolbar button).
  function markRowFocus(title) {
    const wrap = tableWrapRef.current;
    const btn = wrap ? [...wrap.querySelectorAll('button[aria-label]')].find((b) => b.getAttribute('aria-label') === `Actions for ${title}`) : null;
    rowFocusRef.current = btn ?? wrap?.querySelector('[role="region"][tabindex]') ?? null;
  }

  function startEdit(rule) {
    const evt = classic.catalog.find((e) => e.id === rule.eventId);
    const params = {};
    (evt?.params ?? []).forEach((p) => { params[p.key] = rule.params?.[p.key] ?? p.default; });
    setNotice(null);
    setEditError(null);
    setEditing({ ruleId: rule.ruleId, eventId: rule.eventId, params, recipients: rule.recipients ?? [] });
  }

  async function saveRule(override) {
    if (savingRef.current) return;
    savingRef.current = true;
    const src = override ?? editing;
    setEditError(null);
    setBusyRule(true);
    const body = { eventId: src.eventId, params: src.params, recipients: src.recipients };
    try {
      if (src.ruleId) await api.updateNotification(customerId, src.ruleId, body);
      else await api.createNotification(customerId, body);
      setEditing(null);
      setNotice({ tone: 'ok', text: 'Notification rule saved.' });
      await onChanged();
    } catch (e) {
      setEditError(e.message);
    } finally {
      savingRef.current = false;
      setBusyRule(false);
    }
  }

  async function removeRule() {
    setBusyRule(true);
    try {
      await api.deleteNotification(customerId, deleting.rule.ruleId);
      setDeleting(null);
      await onChanged();
    } catch (e) {
      setDeleting(null);
      setNotice({ tone: 'error', text: e.message });
    } finally {
      setBusyRule(false);
    }
  }

  async function sendTest() {
    const email = (testEmail ?? '').trim();
    if (!email) return;
    setBusyTest(true);
    try {
      const res = await api.sendNotificationTest(customerId, email);
      setTestEmail(null);
      setNotice({ tone: 'ok', text: `Test sent (${res.sent}).` });
      await onChanged();
    } catch (e) {
      setTestEmail(null);
      setNotice({ tone: 'error', text: e.message });
    } finally {
      setBusyTest(false);
    }
  }

  if (data.errors.classic) return <div className="card border-red-200 text-red-700" role="alert">{data.errors.classic}</div>;
  if (!classic) return <PageState state="loading" header={false} cards={0} label="Loading the rules" />;

  const columns = [
    {
      id: 'rule',
      header: 'Rule',
      sortValue: (r) => r.title.toLowerCase(),
      cell: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-slate-900">{r.title}</div>
          {r.category ? <div className="text-xs text-slate-600">{CATEGORY_LABELS[r.category] ?? r.category}</div> : null}
        </div>
      ),
    },
    { id: 'source', header: 'Source', hideBelow: 'md', sortValue: (r) => SOURCE[r.source], cell: (r) => <span className="badge-slate">{SOURCE[r.source]}</span> },
    { id: 'audience', header: 'Audience', cell: (r) => <RoleChips roles={r.audience} max={2} label={`Audience of ${r.title}`} /> },
    { id: 'cadence', header: 'Cadence', hideBelow: 'lg', cell: (r) => <span title={r.cadence.hint}>{r.cadence.label}</span> },
    {
      id: 'enabled',
      header: 'Enabled',
      sortValue: (r) => (r.enabled ? 1 : 0),
      cell: (r) => {
        const on = Object.hasOwn(pending, r.id) ? pending[r.id] : r.enabled;
        const dep = r.dependency ?? { available: true };
        // An unavailable rule cannot be switched on; one already on (paused) can still be switched off.
        const blocked = !dep.available && !on;
        const reasonId = `dep-${r.id}`;
        return (
          <div className={blocked ? 'text-slate-500' : undefined}>
            <Toggle label="" ariaLabel={`${r.title}: ${SOURCE[r.source]} rule`} describedBy={dep.available ? undefined : reasonId} checked={on} disabled={!masterOn || r.locked || blocked} onChange={(next) => toggle(r, next)} />
            {r.locked ? <span className="block text-xs text-slate-600">Always on</span> : null}
            {dep.available ? null : (
              <span id={reasonId} className={`block max-w-[16rem] text-xs ${on ? 'font-medium text-slate-900' : 'text-slate-600'}`}>
                {on ? <><Dot tone="warn" />{' '}{dep.paused}</> : dep.reason}
                {dep.link === 'data-collection' ? <>{' '}<Link className="font-semibold text-ipc-light hover:underline" to={`${customerSectionPath(customerId, 'data-collection')}#sources`}>Open Data collection</Link></> : null}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  const categories = [...new Set(rows.filter((r) => r.source === 'live' && r.category).map((r) => r.category))];
  const filters = [
    { id: 'source', label: 'Source', options: [{ value: 'daily', label: SOURCE.daily }, { value: 'live', label: SOURCE.live }].filter((o) => rows.some((r) => r.source === o.value)), test: (r, v) => r.source === v },
    { id: 'state', label: 'State', options: [{ value: 'on', label: 'On' }, { value: 'off', label: 'Off' }], test: (r, v) => (v === 'on') === r.enabled },
    ...(categories.length ? [{ id: 'category', label: 'Category', options: categories.map((c) => ({ value: c, label: CATEGORY_LABELS[c] ?? c })), test: (r, v) => r.category === v }] : []),
  ];

  const canSettings = !operatorAccount && Boolean(data.watch?.settings);
  const rowActions = (r) => (r.source === 'daily'
    ? [
      { id: 'edit', label: 'Edit', onSelect: () => { markRowFocus(r.title); startEdit(r.rule); } },
      { id: 'delete', label: 'Delete', danger: true, onSelect: () => { markRowFocus(r.title); setDeleting(r); } },
    ]
    : [{ id: 'audiences', label: 'Edit audiences', hidden: r.locked || !canSettings, onSelect: () => setSettingsOpen(true) }]);

  return (
    <div className="space-y-3">
      {notice ? <Notice tone={notice.tone === 'error' ? 'bad' : (notice.tone === 'warn' ? 'warn' : 'good')} role={notice.tone === 'error' ? 'alert' : 'status'} titleAs="p" title={notice.text} /> : null}
      {!masterOn ? <Notice tone="warn" titleAs="p" title="Notifications are off: no rule sends email.">Switch Notifications on above to start. Your selections are kept.</Notice> : null}
      {operatorAccount ? <p className="text-sm text-ipc-muted">Live watch rules are visible to the customer&apos;s onGuard administrators only.</p> : null}
      <div ref={tableWrapRef}>
      <ConsoleTable
        label="Rules"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        rowName={(r) => r.title}
        searchText={ruleSearchText}
        searchPlaceholder="Search rules"
        filters={filters}
        renderExpanded={(r) => <Detail row={r} classicCatalog={classic.catalog} />}
        rowActions={rowActions}
        emptyState={{ title: 'No rules yet', body: 'Create a daily rule to be told about changes after each collection.', action: <button type="button" className="btn-primary" onClick={() => { setEditError(null); rowFocusRef.current = null; setEditing({ eventId: '', params: {}, recipients: [] }); }}>New daily rule</button> }}
        actions={(
          <>
            {canSettings ? <button ref={openerRef} type="button" className="btn-secondary" onClick={() => setSettingsOpen(true)}>Live watch settings</button> : null}
            <button ref={testRef} type="button" className="btn-secondary" disabled={!masterOn} onClick={() => setTestEmail(config?.alertEmail ?? '')}>Send test email</button>
            <button ref={newRuleRef} type="button" className="btn-primary" onClick={() => { setNotice(null); setEditError(null); rowFocusRef.current = null; setEditing({ eventId: '', params: {}, recipients: [] }); }}>New daily rule</button>
          </>
        )}
      />
      </div>
      {editing ? (
        <Dialog open wide returnFocusRef={editing.ruleId ? rowFocusRef : newRuleRef} title={editing.ruleId ? 'Edit daily rule' : 'New daily rule'} onClose={() => setEditing(null)}>
          <DialogError message={editError} />
          <RuleEditor editing={editing} catalog={classic.catalog} onChange={setEditing} onSave={saveRule} onCancel={() => setEditing(null)} busy={busyRule} />
        </Dialog>
      ) : null}
      {deleting ? (
        <Dialog
          open
          title="Delete rule"
          returnFocusRef={rowFocusRef}
          onClose={() => setDeleting(null)}
          footer={(
            <>
              <button type="button" className="btn-secondary" onClick={() => setDeleting(null)} disabled={busyRule}>Cancel</button>
              <button type="button" className="btn-danger" onClick={removeRule} disabled={busyRule}>Delete rule</button>
            </>
          )}
        >
          <p>Delete the rule &quot;{deleting.title}&quot;? It stops sending at once.</p>
        </Dialog>
      ) : null}
      {settingsOpen && canSettings ? (
        <LiveWatchSettingsDialog
          customerId={customerId}
          watch={data.watch}
          returnFocusRef={openerRef}
          onClose={() => setSettingsOpen(false)}
          onSaved={async (r) => {
            if (r?.live?.reason) setNotice({ tone: 'warn', text: LIVE_REASON_TEXT[r.live.reason] ?? 'Saved.' });
            await onChanged();
          }}
        />
      ) : null}
      {testEmail !== null ? (
        <Dialog
          open
          title="Send a test email"
          returnFocusRef={testRef}
          onClose={() => setTestEmail(null)}
          footer={(
            <>
              <button type="button" className="btn-secondary" onClick={() => setTestEmail(null)} disabled={busyTest}>Cancel</button>
              <button type="button" className="btn-primary" onClick={sendTest} disabled={busyTest || testEmail.trim() === ''}>Send test</button>
            </>
          )}
        >
          <label className="block text-sm">
            <span className="label">Send a test email to</span>
            <input type="email" className="input" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" />
          </label>
          <p className="text-xs text-slate-600">A sample daily email with two sample events. It bypasses the Notifications switch because you asked for it.</p>
        </Dialog>
      ) : null}
    </div>
  );
}
